import { createSelector } from "reselect";

import { IRootState } from "../reducers";
import { IUser, IUserState } from "./types";
import {
  IBillingAccount,
  IBillingInfo,
  ICredentialResponse,
  ICustomer,
  IUserResponseData,
} from "../../services/api/user/types";
import { RECURLY } from "../../constants/spCode";

const selectUserState = (state: IRootState): IUserState => {
  return state.user;
};

export const selectUserData = createSelector(
  [selectUserState],
  (userState: IUserState): IUser => {
    return userState.data;
  },
);

export const selectIsUserReady = createSelector(
  [selectUserState],
  (user): boolean => {
    return user.isUserReady;
  },
);

export const selectPrincipalData = createSelector(
  [selectUserState],
  (userState: IUserState): IUserResponseData | undefined => {
    return userState.principalData;
  },
);

export const selectCustomerData = createSelector(
  [selectUserState],
  (userState: IUserState): ICustomer | undefined => {
    return userState.customerData;
  },
);

export const selectBillingAccountList = createSelector(
  [selectUserState],
  (userState: IUserState): IBillingAccount[] | undefined => {
    return userState.billingAccountList;
  },
);

export const selectIsBillingAccountListLoaded = createSelector(
  [selectUserState],
  (userState: IUserState): boolean => {
    return userState.isBillingAccountListLoaded;
  },
);

export const selectRecurlyBillingAccount = createSelector(
  [selectBillingAccountList],
  (billingAccountList): IBillingAccount | undefined => {
    return billingAccountList?.find((item) => {
      return item.sp_code === RECURLY;
    });
  },
);

export const selectIsFetchingPrincipalAndCustomer = createSelector(
  [selectUserState],
  (userState: IUserState): boolean => {
    return userState.isFetchingPrincilal || userState.isFetchingCustomer;
  },
);

export const selectBillingData = createSelector(
  [selectUserState],
  (userState: IUserState): IBillingInfo | undefined => {
    return userState.billingData;
  },
);

export const selectIsBillingDataLoaded = createSelector(
  [selectUserState],
  (userState: IUserState): boolean => {
    return userState.isBillingDataLoaded;
  },
);

export const selectBillingDataBySpCode = createSelector(
  [selectUserState],
  (userState: IUserState): Record<string, IBillingInfo> => {
    return userState.billingDataBySpCode ?? {};
  },
);

export const selectPaymentCountry = createSelector(
  [selectUserState],
  (userState: IUserState): string | undefined => {
    return userState.country;
  },
);

export const selectPaymentPostalCode = createSelector(
  [selectUserState],
  (userState: IUserState): string | undefined => {
    return userState.postalCode;
  },
);

export const selectPaymentRegion = createSelector(
  [selectUserState],
  (userState: IUserState): string | undefined => {
    return userState.region;
  },
);

export const selectCredentials = createSelector(
  [selectUserState],
  (userState: IUserState): ICredentialResponse[] | undefined => {
    return userState.credentials;
  },
);

export const selectActiveAppleCredential = createSelector(
  [selectCredentials],
  (credentials): ICredentialResponse | undefined => {
    return credentials?.find((c) => {
      return c.authority_code === "APPLE" && c.is_active;
    });
  },
);

export const selectActiveGoogleCredential = createSelector(
  [selectCredentials],
  (credentials): ICredentialResponse | undefined => {
    return credentials?.find((c) => {
      return c.authority_code === "GOOGLE" && c.is_active;
    });
  },
);

export const selectActiveLabeledDeviceCredentials = createSelector(
  [selectCredentials],
  (credentials): ICredentialResponse[] => {
    return (
      credentials?.filter((c) => {
        return c.kind === "DEVICE_PASSWORD" && c.is_active && !!c.device_label;
      }) ?? []
    );
  },
);
